import {
    Avatar,
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Button,
    Divider,
    Flex,
    Grid,
    GridItem,
    Heading,
    Spinner,
    Stack,
    Text,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { onGetSchoolById, onGetStudentsBySchool } from "../../http/school";
import { School } from "../../interfaes/school";
import { Student } from "../../interfaes/student";
import MaterialTable, { Column } from "material-table";
import { createTheme, ThemeProvider } from '@mui/material';
import { AiOutlineArrowLeft } from "react-icons/ai";

import { Roles } from "../../interfaes/user";
import ModernLoader from "../../components/ModernLoader";

const defaultMaterialTheme = createTheme();

export default function SchoolDetail() {
    const { id } = useParams<{ id: string }>();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation(['school', 'studentLocal']);

    const [school, setSchool] = useState<School | null>(state as School || null);
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(true);

    const calculateAgeFromTimestamp = (timestamp: number): number => {
        const birthDate = new Date(timestamp);
        const currentDate = new Date();
        let age = currentDate.getFullYear() - birthDate.getFullYear();
        if (
            currentDate.getMonth() < birthDate.getMonth() ||
            (currentDate.getMonth() === birthDate.getMonth() &&
                currentDate.getDate() < birthDate.getDate())
        ) {
            age--;
        }
        return age;
    }

    useEffect(() => {
        if (id) {
            fetchData(id);
        }
    }, [id]);

    const fetchData = async (schoolId: string) => {
        setLoading(true);
        try {
            const [schoolRes, studentsRes] = await Promise.all([
                onGetSchoolById(schoolId),
                onGetStudentsBySchool(schoolId)
            ]);
            setSchool(schoolRes.data);
            setStudents(studentsRes.data);
        } catch (error) {
            console.error("Error fetching school details:", error);
        } finally {
            setLoading(false);
        }
    };

    const studentColumns: Column<Student>[] = [
        {
            title: t('studentLocal:name'),
            field: 'fullName',
            render: rowData => (
                <Flex align="center" cursor="pointer" onClick={() => navigate(`/students-profile/${rowData.studentId}`)}>
                    <Avatar name={rowData.fullName} src={rowData.studentImage} mr={3} w={'40px'} h={'40px'} background={'#e2e8f0'} />
                    <Text fontWeight="medium" color="blue.600" _hover={{ textDecoration: 'underline' }}>
                        {rowData.fullName}
                    </Text>
                </Flex>
            ),
            customFilterAndSearch: (term, rowData) => (rowData.fullName).indexOf(term) !== -1
        },
        {
            title: t('studentLocal:age'),
            field: 'dob',
            render: rowData => calculateAgeFromTimestamp(rowData.dob)
        },
        {
            title: t('studentLocal:manageStudents.gender'),
            field: 'gender',
        },
        {
            title: t('studentLocal:teacherName'),
            render: rowData => rowData?.relationships?.find((r) => r.relation === Roles.TEACHER)?.relatedUser?.displayName || '-'
        },
        {
            title: t('studentLocal:parentsName'),
            render: rowData => rowData?.relationships?.find((r) => r.relation === Roles.PARENT)?.relatedUser?.displayName || '-'
        },
        {
            title: t('studentLocal:manageStudents.address'),
            render: rowData => `${rowData.localAddress || ''}, ${rowData.district || ''}, ${rowData.province || ''}`
        }
    ];

    if (loading && !school) {
        return (
            <Flex justify="center" align="center" h="50vh">
                <Spinner size="xl" color="blue.500" />
            </Flex>
        );
    }

    if (!school) {
        return <Box p={8}><Text color="red.500">School not found.</Text></Box>;
    }

    return (
        <Box animation="fadeIn 0.5s ease-in-out">
            <Flex justify="space-between" align="center" mb={6}>
                <Breadcrumb fontSize="sm" color="gray.600">
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => navigate('/school')}>Schools</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem isCurrentPage>
                        <BreadcrumbLink>{school.name}</BreadcrumbLink>
                    </BreadcrumbItem>
                </Breadcrumb>
                <Button
                    leftIcon={<AiOutlineArrowLeft />}
                    variant="ghost"
                    colorScheme="blue"
                    onClick={() => navigate('/school')}
                >
                    Back to List
                </Button>
            </Flex>

            <Box bg="white" p={8} borderRadius="2xl" boxShadow="xl" mb={8} border="1px solid" borderColor="gray.100">
                <Flex justify="space-between" align="start" wrap="wrap" gap={6}>
                    <Stack spacing={4} flex="1" minW="300px">
                        <Heading size="xl" color="blue.800" letterSpacing="tight">
                            {school.name}
                        </Heading>
                        <Text fontSize="lg" color="gray.500" fontWeight="medium">
                            {school.localAddress}, {school.localLevel}, {school.district}, {school.province}
                        </Text>
                    </Stack>

                    <Grid templateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap={8} w="full" bg="gray.50" p={6} borderRadius="xl">
                        <GridItem>
                            <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" mb={1}>
                                {t('IEMISNumber')}
                            </Text>
                            <Text fontSize="md" fontWeight="bold" color="gray.700">
                                {school.IEMISNumber || '-'}
                            </Text>
                        </GridItem>
                        <GridItem>
                            <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" mb={1}>
                                Province
                            </Text>
                            <Text fontSize="md" color="gray.700">{school.province}</Text>
                        </GridItem>
                        <GridItem>
                            <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" mb={1}>
                                District
                            </Text>
                            <Text fontSize="md" color="gray.700">{school.district}</Text>
                        </GridItem>
                        <GridItem>
                            <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase" mb={1}>
                                Local Level
                            </Text>
                            <Text fontSize="md" color="gray.700">{school.localLevel}</Text>
                        </GridItem>
                    </Grid>
                </Flex>
            </Box>

            <Box bg="white" borderRadius="2xl" boxShadow="lg" overflow="hidden">
                <Box p={6} borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
                    <Heading size="md" color="gray.700">
                        {t('studentLocal:studentsInThisSchool', { defaultValue: 'Students in this school' })}
                    </Heading>
                </Box>

                <Box p={4}>
                    <ThemeProvider theme={defaultMaterialTheme}>
                        <MaterialTable
                            title=""
                            columns={studentColumns}
                            data={students}
                            isLoading={loading}
                            options={{
                                search: true,
                                sorting: true,
                                paging: true,
                                pageSize: 20,
                                paginationPosition: 'both',
                                headerStyle: {
                                    backgroundColor: '#f8fafc',
                                    fontWeight: 'bold',
                                    color: '#64748b'
                                }
                            }}
                            components={{
                                OverlayLoading: (props) => (
                                    <Box
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        right={0}
                                        bottom={0}
                                        zIndex={100}
                                        bg="whiteAlpha.700"
                                        display={loading ? "flex" : "none"}
                                        alignItems="flex-start"
                                        justifyContent="center"
                                        pt="80px"
                                    >
                                        <ModernLoader h="150px" message="Fetching students..." />
                                    </Box>
                                )
                            }}
                            onRowClick={(_, rowData) => navigate(`/students-profile/${rowData?.studentId}`)}
                        />
                    </ThemeProvider>
                </Box>
            </Box>
        </Box>
    );
}
